import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, collection, getDocs, query, where } from 'firebase/firestore';
import { auth } from '../firebase';
import { db } from '../firebase';
import { useBranding } from '../TournamentBrandingContext';
import {
  Box, Container, Typography, Card, CardContent, Button,
  TextField, RadioGroup, FormControlLabel, Radio, CircularProgress, Checkbox,
  Alert, Divider, Breadcrumbs, Link, Grid, Chip, MenuItem,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import PeopleIcon from '@mui/icons-material/People';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LockIcon from '@mui/icons-material/Lock';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { logError } from '../utils/errorLogger';

const FUNCTIONS_BASE = 'https://us-central1-torch-beach-app.cloudfunctions.net';

function formatPhone(raw) {
  let d = raw.replace(/\D/g, '');
  if (d.startsWith('1')) d = d.slice(1);
  if (d.length <= 3) return d;
  if (d.length <= 6) return `(${d.slice(0,3)}) ${d.slice(3)}`;
  return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6,10)}`;
}
function emailValid(v) { return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim()); }
function phoneValid(v) { const d = v.replace(/\D/g, ''); return d.length === 10 || (d.length === 11 && d[0] === '1'); }

export default function TournamentRegister() {
  const { tid, division } = useParams();
  const navigate = useNavigate();
  const divName = decodeURIComponent(division);
  const b = useBranding();
  const heroBg = `linear-gradient(135deg, ${b.primary} 0%, ${b.secondary} 100%)`;

  const [tournament, setTournament] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [touched, setTouched] = useState({});
  const [isFreeClinic, setIsFreeClinic] = useState(false);
  const [waveConflict, setWaveConflict] = useState(null);
  // The juniors on this account. A parent with more than one was given the
  // first child the query happened to return and had to notice and retype the
  // other — visible, so recoverable, but only if they looked.
  const [myJuniors, setMyJuniors] = useState([]);
  const [checkingFree, setCheckingFree] = useState(false);
  // If the typed email matches an existing user account, we surface a
  // login-required alert and block submit. The check fires on email blur
  // (and on subsequent edits, debounced).
  const [existingUser, setExistingUser] = useState(null); // { firstName, lastName } or null
  const [checkingEmail, setCheckingEmail] = useState(false);

  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '', smsConsent: false,
    partner_first_name: '', partner_last_name: '', partner_email: '', partner_phone: '', partner_membership_number: '',
    payment_option: 'full', membership_number: '',
  });

  useEffect(() => {
    async function load() {
      try {
        const snap = await getDoc(doc(db, 'tournaments', tid));
        if (snap.exists()) setTournament({ id: snap.id, ...snap.data() });
      } catch (e) { console.error(e); logError('TournamentRegister', e.message || String(e), e.stack || ''); }
      finally { setLoading(false); }
    }
    load();
  }, [tid]);

  // Auto-fill Player 1 from logged-in user profile
  useEffect(() => {
    const user = auth.currentUser;
    if (!user) return;
    const fillFromProfile = async () => {
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (!userDoc.exists()) return;
        const d = userDoc.data();
        const roles = d.roles || [];
        const isJuniorParent = roles.includes('parent') && !roles.includes('adult_athlete');

        if (isJuniorParent) {
          // Both links: parentUserId is the legacy single parent, parentUserIds
          // the array that replaced it. Querying only the first gave a
          // co-parent their OWN name as the player, which is the mistake the
          // junior league form used to make for everybody.
          const [byParent, byArray] = await Promise.all([
            getDocs(query(collection(db, 'athletes'), where('parentUserId', '==', user.uid))),
            getDocs(query(collection(db, 'athletes'), where('parentUserIds', 'array-contains', user.uid))),
          ]);
          const byId = new Map();
          [...byParent.docs, ...byArray.docs].forEach(doc0 => {
            const a = doc0.data();
            if (a.isArchived !== true) byId.set(doc0.id, { id: doc0.id, ...a });
          });
          const kids = [...byId.values()]
            .sort((a, b) => String(a.firstName || '').localeCompare(String(b.firstName || '')));
          setMyJuniors(kids);
          if (kids.length) {
            // Prefilled with the first alphabetically when there are several;
            // the picker below is how the right one gets chosen.
            const junior = kids[0];
            setForm(f => ({
              ...f,
              first_name: junior.firstName || f.first_name,
              last_name: junior.lastName || f.last_name,
              email: user.email || f.email,
              phone: formatPhone(d.phone || f.phone),
            }));
          } else {
            // No juniors yet — use parent info
            setForm(f => ({
              ...f,
              first_name: d.firstName || f.first_name,
              last_name: d.lastName || f.last_name,
              email: user.email || f.email,
              phone: formatPhone(d.phone || f.phone),
            }));
          }
        } else {
          // Adult/coach/admin — use their own info
          setForm(f => ({
            ...f,
            first_name: d.firstName || f.first_name,
            last_name: d.lastName || f.last_name,
            email: user.email || f.email,
            phone: formatPhone(d.phone || f.phone),
          }));
        }
      } catch (e) {
        console.error('Auto-fill failed:', e);
      }
    };
    fillFromProfile();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Check free clinic eligibility when email changes
  useEffect(() => {
    if (!tournament || !form.email || !emailValid(form.email)) return;
    const freeIfRegistered = tournament.free_if_registered || {};
    if (!freeIfRegistered[divName]) return;

    const timer = setTimeout(async () => {
      setCheckingFree(true);
      try {
        const snap = await getDocs(query(
          collection(db, 'registrations'),
          where('tournament_id', '==', tid),
          where('email', '==', form.email.trim().toLowerCase()),
          where('status', '==', 'active')
        ));
        const hasOtherReg = snap.docs.some(d => d.data().division !== divName);
        setIsFreeClinic(hasOtherReg);
        if (hasOtherReg) {
          setForm(f => ({ ...f, payment_option: 'later' }));
        }
      } catch {}
      finally { setCheckingFree(false); }
    }, 600); // debounce 600ms

    return () => clearTimeout(timer);
  }, [form.email, tournament, divName, tid]);

  // Look up the typed email in the users collection. If a match is found we
  // surface an "account exists, please log in" prompt and block submit. This
  // runs whenever the email changes (debounced) AND only when the user has
  // already touched the email field (so we don't flash the prompt before
  // they're done typing). Skips entirely when the user is already logged in.
  useEffect(() => {
    if (auth.currentUser) return; // logged-in users don't need this prompt
    if (!touched.email) return;
    if (!emailValid(form.email)) {
      setExistingUser(null);
      return;
    }
    const e = form.email.trim().toLowerCase();
    const timer = setTimeout(async () => {
      setCheckingEmail(true);
      try {
        const snap = await getDocs(query(
          collection(db, 'users'),
          where('email', '==', e)
        ));
        if (!snap.empty) {
          const d = snap.docs[0].data();
          setExistingUser({
            firstName: d.firstName || d.displayName?.split(' ')[0] || '',
            lastName: d.lastName || '',
          });
        } else {
          setExistingUser(null);
        }
      } catch (err) {
        // Non-fatal: on any error treat as no match (fail-open) so we don't
        // block legitimate guest registrations because of an infra glitch.
        console.warn('Email lookup failed:', err);
        setExistingUser(null);
      }
      setCheckingEmail(false);
    }, 600); // debounce 600ms
    return () => clearTimeout(timer);
  }, [form.email, touched.email]);

  function priceBreakdown() {
    if (isFreeClinic || !tournament) return { base: 0, lateFee: 0, isLate: false, total: 0 };
    const prices = typeof tournament.prices === 'string'
      ? JSON.parse(tournament.prices) : (tournament.prices || {});
    const base = Number(prices[divName] || 0);
    let lateFee = 0;
    let isLate = false;
    if (tournament.late_date) {
      const lateDt = new Date(`${tournament.late_date}T${tournament.late_time || '00:00'}`);
      if (new Date() >= lateDt) {
        const lateFees = typeof tournament.late_fees === 'string'
          ? JSON.parse(tournament.late_fees) : (tournament.late_fees || {});
        lateFee = lateFees[divName] != null ? Number(lateFees[divName]) : 0;
        if (lateFee > 0) isLate = true;
      }
    }
    return { base, lateFee, isLate, total: base + lateFee };
  }

  function price() {
    return priceBreakdown().total;
  }

  function handleChange(e) {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: name === 'phone' ? formatPhone(value) : value }));
  }
  function handleBlur(e) { setTouched(t => ({ ...t, [e.target.name]: true })); }

  async function handleSubmit(e) {
    e.preventDefault();
    setTouched({ first_name: true, last_name: true, email: true, phone: true, membership_number: true });
    if (!form.first_name.trim() || !form.last_name.trim()) return;
    if (!emailValid(form.email)) return;
    if (!phoneValid(form.phone)) return;
    // Block submit if the typed email belongs to an existing user account —
    // the inline alert directs them to log in first.
    if (existingUser) {
      setError('An account exists with this email. Please log in to continue.');
      return;
    }
    if (tournament?.sanctioning_body && tournament.sanctioning_body !== 'None' && !form.membership_number.trim()) {
      setError(`Please enter your ${tournament.sanctioning_body} membership number.`);
      return;
    }
    setSubmitting(true); setError('');
    try {
      // Check for duplicates — player 1 or partner already registered in this division
      try {
        const dupSnap = await getDocs(query(
          collection(db, 'registrations'),
          where('tournament_id', '==', tid),
          where('division', '==', divName),
          where('status', '==', 'active')
        ));
        const formP1Name = `${form.first_name.trim().toLowerCase()} ${form.last_name.trim().toLowerCase()}`;
        const formP1Email = (form.email || '').trim().toLowerCase();
        const formP2Name = (form.partner_first_name.trim() || form.partner_last_name.trim())
          ? `${form.partner_first_name.trim().toLowerCase()} ${form.partner_last_name.trim().toLowerCase()}`
          : '';
        const formP2Email = (form.partner_email || '').trim().toLowerCase();
        for (const d of dupSnap.docs) {
          const r = d.data();
          const existingP1Name = `${(r.first_name || '').toLowerCase()} ${(r.last_name || '').toLowerCase()}`.trim();
          const existingP1Email = (r.email || '').toLowerCase();
          const existingP2Name = `${(r.partner_first_name || '').toLowerCase()} ${(r.partner_last_name || '').toLowerCase()}`.trim();
          const existingP2Email = (r.partner_email || '').toLowerCase();
          const existingNames = [existingP1Name, existingP2Name].filter(Boolean);
          const existingEmails = [existingP1Email, existingP2Email].filter(Boolean);
          // Check player 1 against existing
          if (formP1Email && existingEmails.includes(formP1Email)) {
            setError(`${form.first_name} ${form.last_name} is already registered in this division.`);
            setSubmitting(false);
            return;
          }
          if (formP1Name && existingNames.includes(formP1Name)) {
            setError(`${form.first_name} ${form.last_name} is already registered in this division.`);
            setSubmitting(false);
            return;
          }
          // Check partner against existing
          if (formP2Email && existingEmails.includes(formP2Email)) {
            setError(`${form.partner_first_name} ${form.partner_last_name} is already registered in this division.`);
            setSubmitting(false);
            return;
          }
          if (formP2Name && existingNames.includes(formP2Name)) {
            setError(`${form.partner_first_name} ${form.partner_last_name} is already registered in this division.`);
            setSubmitting(false);
            return;
          }
        }
      } catch (dupErr) {
        console.warn('Duplicate check failed (non-blocking):', dupErr);
      }
      const res = await fetch(`${FUNCTIONS_BASE}/createRegistration`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tournament_id: tid, division: divName, ...form }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Something went wrong.'); return; }
      if (data.checkout_url) { window.location.href = data.checkout_url; }
      else { navigate(`/tournament-success?reg_id=${data.reg_id}`); }
    } catch { setError('Network error. Please try again.'); }
    finally { setSubmitting(false); }
  }

  if (loading) return <Box sx={{ textAlign: 'center', py: 8 }}><CircularProgress /></Box>;
  if (!tournament) return (
    <Container sx={{ textAlign: 'center', py: 8 }}>
      <Typography variant="h5" color="text.secondary" gutterBottom>Tournament not found.</Typography>
      <Button variant="contained" onClick={() => navigate('/tournaments')}>Back to Tournaments</Button>
    </Container>
  );

  const breakdown = priceBreakdown();
  const p = breakdown.total;
  const half = p / 2;
  const isClinicWithFreeOption = (tournament.free_if_registered || {})[divName];

  return (
    <Box>
      <Box sx={{ background: heroBg, color: b.banner_color, py: 5 }}>
        <Container maxWidth="md">
          <Breadcrumbs sx={{ mb: 1, '& *': { color: `${b.banner_color}99` } }}>
            <Link underline="hover" sx={{ cursor: 'pointer', color: `${b.banner_color}99 !important` }} onClick={() => navigate('/tournaments')}>Tournaments</Link>
            <Link underline="hover" sx={{ cursor: 'pointer', color: `${b.banner_color}99 !important` }} onClick={() => navigate(`/tournament/${tid}`)}>{tournament.name}</Link>
            <Typography sx={{ color: b.banner_color }}>{divName} – Register</Typography>
          </Breadcrumbs>
          <Typography variant="h3" fontWeight="bold" gutterBottom sx={{ color: b.banner_color }}>Register</Typography>
          <Typography variant="h6" sx={{ opacity: 0.85, color: b.banner_color }}>
            {tournament.name} – <strong>{divName} Division</strong>
          </Typography>
        </Container>
      </Box>

      <Container maxWidth="sm" sx={{ py: 5 }}>
        {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

        {/* Free clinic notice */}
        {isClinicWithFreeOption && (
          <Alert severity="info" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
            <strong>Free with tournament registration!</strong> Enter the same email you used to register for another division and this clinic will be free.
          </Alert>
        )}

        {/* Late registration notice */}
        {breakdown.isLate && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <strong>Late registration:</strong> a ${breakdown.lateFee.toFixed(2)} late fee has been added.
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
              Base ${breakdown.base.toFixed(2)} + Late ${breakdown.lateFee.toFixed(2)} = <strong>${breakdown.total.toFixed(2)}</strong>
            </Typography>
          </Alert>
        )}

        {/* Registration closed check */}
        {tournament.close_date && (() => {
          const closeDt = new Date(`${tournament.close_date}T${tournament.close_time || '23:59'}`);
          if (new Date() > closeDt) return (
            <Alert severity="error" sx={{ mb: 3 }}>
              <strong>Registration is closed.</strong> The registration deadline for this tournament has passed.
            </Alert>
          );
          return null;
        })()}

        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <form onSubmit={handleSubmit} noValidate>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PersonIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  {myJuniors.length ? 'Player 1' : 'Player 1 (You)'}
                </Typography>
              </Box>
              {/* More than one junior on the account: say which is entering,
                  rather than prefilling one and hoping the parent notices. */}
              {myJuniors.length > 1 && (
                <TextField select fullWidth size="small" sx={{ mb: 1.5 }}
                  label="Which player is entering?"
                  value={myJuniors.find(j => (j.firstName || '') === form.first_name
                    && (j.lastName || '') === form.last_name)?.id || ''}
                  onChange={e => {
                    const j = myJuniors.find(x => x.id === e.target.value);
                    if (j) setForm(f => ({ ...f, first_name: j.firstName || '', last_name: j.lastName || '' }));
                  }}
                  SelectProps={{ displayEmpty: true }} InputLabelProps={{ shrink: true }}
                  helperText="Or type a different name below">
                  {myJuniors.map(j => (
                    <MenuItem key={j.id} value={j.id}>{j.firstName} {j.lastName}</MenuItem>
                  ))}
                  <MenuItem value="">Someone else…</MenuItem>
                </TextField>
              )}
              <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="First Name" name="first_name" required value={form.first_name}
                             onChange={handleChange} onBlur={handleBlur}
                             error={touched.first_name && !form.first_name.trim()}
                             helperText={touched.first_name && !form.first_name.trim() ? 'Required' : ''} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Last Name" name="last_name" required value={form.last_name}
                             onChange={handleChange} onBlur={handleBlur}
                             error={touched.last_name && !form.last_name.trim()}
                             helperText={touched.last_name && !form.last_name.trim() ? 'Required' : ''} />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Email" name="email" type="email" required value={form.email}
                             onChange={handleChange} onBlur={handleBlur}
                             error={touched.email && !emailValid(form.email)}
                             helperText={touched.email && !emailValid(form.email) ? 'Valid email required' : 'Used for receipt'}
                             InputProps={{
                               endAdornment: (checkingFree || checkingEmail) ? <CircularProgress size={16} /> : null
                             }}
                  />
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <TextField fullWidth label="Mobile Number" name="phone" required value={form.phone}
                             onChange={handleChange} onBlur={handleBlur}
                             error={touched.phone && !phoneValid(form.phone)}
                             helperText={touched.phone && !phoneValid(form.phone) ? 'Valid US phone required' : ''}
                             placeholder="(555) 867-5309" />
                  {/* The number has always been required and never explained.
                      Optional, and only ever a yes — an unticked box is not a
                      refusal, it is somebody who did not answer. */}
                  <FormControlLabel
                    sx={{ mt: 0.5, '.MuiFormControlLabel-label': { fontSize: '0.8rem', color: '#555' } }}
                    control={<Checkbox size="small" checked={form.smsConsent === true}
                      onChange={e => setForm(f => ({ ...f, smsConsent: e.target.checked }))} />}
                    label="Text me about schedule changes and rain calls" />
                </Grid>
                {/* Existing-account prompt. Surfaces below the contact row when
                    the typed email matches a users doc. Blocks submit (see
                    handleSubmit) until the user logs in or changes the email. */}
                {existingUser && (
                  <Grid size={{ xs: 12 }}>
                    <Alert
                      severity="info"
                      action={
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => {
                            // Return here after login. The current path
                            // includes the division segment, so this lands
                            // them right back on the same registration form.
                            sessionStorage.setItem('postLoginReturnTo', window.location.pathname);
                            navigate('/login');
                          }}
                        >
                          Log In
                        </Button>
                      }
                    >
                      An account already exists for <strong>{form.email}</strong>
                      {existingUser.firstName ? ` (${existingUser.firstName}${existingUser.lastName ? ' ' + existingUser.lastName : ''})` : ''}.
                      Please log in to continue, or use a different email.
                    </Alert>
                  </Grid>
                )}
                {tournament.sanctioning_body && tournament.sanctioning_body !== 'None' && (
                  <Grid size={{ xs: 12 }}>
                    <TextField fullWidth
                      label={`${tournament.sanctioning_body} Membership Number *`}
                      name="membership_number"
                      value={form.membership_number}
                      onChange={handleChange} onBlur={handleBlur}
                      error={touched.membership_number && !form.membership_number.trim()}
                      helperText={touched.membership_number && !form.membership_number.trim() ? 'Required for sanctioned events' : `Required for ${tournament.sanctioning_body} events`}
                      required />
                  </Grid>
                )}
              </Grid>

              {/* Free clinic confirmed */}
              {isFreeClinic && (
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                  <strong>Free registration confirmed!</strong> Your email matches an existing registration for this tournament.
                </Alert>
              )}

              <Divider sx={{ my: 3 }} />
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PeopleIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6" fontWeight="bold">
                  Partner <Typography component="span" variant="body2" color="text.secondary">(optional)</Typography>
                </Typography>
              </Box>
              <Grid container spacing={1.5} sx={{ mb: 1 }}>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Partner's First Name" name="partner_first_name" value={form.partner_first_name} onChange={handleChange} /></Grid>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Partner's Last Name" name="partner_last_name" value={form.partner_last_name} onChange={handleChange} /></Grid>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Partner's Email" name="partner_email" type="email" value={form.partner_email} onChange={handleChange} /></Grid>
                <Grid size={{ xs: 6 }}><TextField fullWidth size="small" label="Partner's Mobile" name="partner_phone" value={form.partner_phone} onChange={e => setForm(f => ({ ...f, partner_phone: formatPhone(e.target.value) }))} placeholder="(555) 867-5309" /></Grid>
                {tournament?.sanctioning_body && tournament.sanctioning_body !== 'None' && (
                  <Grid size={{ xs: 12 }}><TextField fullWidth size="small"
                    label={`Partner's ${tournament.sanctioning_body} Membership #`}
                    name="partner_membership_number"
                    value={form.partner_membership_number}
                    onChange={handleChange} /></Grid>
                )}
              </Grid>
              <Typography variant="caption" color="text.secondary">Leave blank if you don't have a partner yet.</Typography>

              <Divider sx={{ my: 3 }} />

              {/* Payment section — hidden if free */}
              {isFreeClinic ? (
                <Alert severity="success" sx={{ mb: 3 }}>
                  <strong>No payment required.</strong> This clinic is free with your tournament registration.
                </Alert>
              ) : (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <CreditCardIcon color="primary" sx={{ mr: 1 }} />
                    <Typography variant="h6" fontWeight="bold">Payment Option</Typography>
                    {isClinicWithFreeOption && (
                      <Chip label="Free with tournament reg" color="info" size="small" sx={{ ml: 1 }} />
                    )}
                  </Box>

                  <RadioGroup name="payment_option" value={form.payment_option}
                              onChange={e => setForm(f => ({ ...f, payment_option: e.target.value }))}>
                    {[
                      { value: 'full',  label: 'Pay in Full',  sub: `$${p.toFixed(2)} – paid now via Stripe`,                badge: `$${p.toFixed(2)}`,    color: 'success.main' },
                      { value: 'half',  label: 'Pay Half Now', sub: `$${half.toFixed(2)} deposit – balance + $5/player at tournament`, badge: `$${half.toFixed(2)}`, color: 'warning.main' },
                      { value: 'later', label: 'Pay Later',    sub: 'Reserve your spot – full payment + $5/player at tournament', badge: '$0 now',             color: 'text.secondary' },
                    ].map(opt => (
                      <Box key={opt.value} onClick={() => setForm(f => ({ ...f, payment_option: opt.value }))}
                           sx={{ border: '1px solid', borderRadius: 2, p: 2, mb: 1.5, cursor: 'pointer',
                                 borderColor: form.payment_option === opt.value ? b.button_bg : 'divider',
                                 bgcolor: form.payment_option === opt.value ? `${b.button_bg}15` : 'transparent',
                                 display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <FormControlLabel value={opt.value} control={<Radio size="small" />}
                                          label={<Box><Typography variant="body1" fontWeight="semibold">{opt.label}</Typography><Typography variant="caption" color="text.secondary">{opt.sub}</Typography></Box>}
                                          sx={{ m: 0, flexGrow: 1 }} />
                        <Typography variant="body2" fontWeight="bold" color={opt.color}>{opt.badge}</Typography>
                      </Box>
                    ))}
                  </RadioGroup>

                  {(form.payment_option === 'half' || form.payment_option === 'later') && (
                    <Alert severity="warning" sx={{ mb: 2, mt: 0 }}>
                      <Typography variant="caption">
                        Payments made the day of the tournament will be an additional <strong>$5 per player</strong>.
                      </Typography>
                    </Alert>
                  )}
                  <Alert severity="info" icon={<LockIcon />} sx={{ mb: 3, mt: 1 }}>
                    <Typography variant="caption">Paying now? You'll be taken to a secure Stripe checkout page.</Typography>
                  </Alert>
                </>
              )}

              <Button type="submit" variant="contained" fullWidth size="large"
                      endIcon={submitting ? null : <ArrowForwardIcon />}
                      disabled={submitting || (tournament.close_date && new Date() > new Date(`${tournament.close_date}T${tournament.close_time || '23:59'}`))}
                      sx={{ bgcolor: b.button_bg, color: b.button_text, '&:hover': { bgcolor: b.primary } }}>
                {submitting ? <CircularProgress size={24} color="inherit" /> : (isFreeClinic ? 'Complete Registration' : 'Continue')}
              </Button>
            </form>
          </CardContent>
        </Card>
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button startIcon={<ArrowBackIcon />} color="inherit" onClick={() => navigate(`/tournament/${tid}`)}>
            Back to division select
          </Button>
        </Box>
      </Container>

      {b.footer && (
        <Box sx={{ bgcolor: b.primary, color: 'rgba(255,255,255,0.6)', py: 2, textAlign: 'center' }}>
          <Typography variant="caption">{b.footer}</Typography>
        </Box>
      )}
    </Box>
  );
}
